import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Home,
  Layers,
  Image as ImageIcon,
  Building2,
  MapPin,
  Briefcase,
  FileText,
  Tags,
  Award,
  Users as UsersIcon,
  Star,
  Inbox,
  Settings as SettingsIcon,
  ShieldCheck,
  History,
} from "lucide-react";
import { useAuthStore, selectIsSuperAdmin } from "../../store/auth.store.js";
import { useUiStore } from "../../store/ui.store.js";
import { cn } from "../../lib/cn.js";

/**
 * Hierarchical sidebar nav. Groups mirror the conceptual sections of the
 * admin: Catalogue, Content, About, Inbox + (super-admin-only) System.
 *
 * Per Tech Impl § 11 Session 10: super-admin-only items are hidden for
 * content_editor, never just disabled. Server enforces the role gate too.
 */

const SECTIONS = [
  {
    title: null,
    items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard", end: true }],
  },
  {
    title: "Catalogue",
    items: [
      { to: "/designs", icon: Home, label: "Home Designs" },
      { to: "/facades", icon: ImageIcon, label: "Facades" },
      { to: "/floor-plans", icon: Layers, label: "Floor Plans" },
      { to: "/display-homes", icon: Building2, label: "Display Homes" },
      { to: "/house-land", icon: MapPin, label: "House & Land" },
    ],
  },
  {
    title: "Content",
    items: [
      { to: "/services", icon: Briefcase, label: "Services" },
      { to: "/blog-categories", icon: Tags, label: "Blog Categories" },
      { to: "/blogs", icon: FileText, label: "Blogs" },
    ],
  },
  {
    title: "About",
    items: [
      { to: "/awards", icon: Award, label: "Awards" },
      { to: "/team", icon: UsersIcon, label: "Team" },
      { to: "/testimonials", icon: Star, label: "Testimonials" },
    ],
  },
  {
    title: "Inbox",
    items: [{ to: "/forms", icon: Inbox, label: "Form Submissions" }],
  },
  {
    title: "System",
    superAdminOnly: true,
    items: [
      { to: "/settings", icon: SettingsIcon, label: "Settings" },
      { to: "/users", icon: ShieldCheck, label: "Users" },
      { to: "/activity-logs", icon: History, label: "Activity Logs" },
    ],
  },
];

function NavItem({ to, icon: Icon, label, end, collapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
          isActive
            ? "bg-bg-elevated-2 text-accent"
            : "text-text-secondary hover:bg-bg-elevated-2 hover:text-text-primary"
        )
      }
      title={collapsed ? label : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

export function Sidebar() {
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin);
  const collapsed = useUiStore((s) => s.sidebarCollapsed);

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-30 flex h-full flex-col border-r border-border-subtle bg-bg-elevated transition-[width] duration-200",
        collapsed ? "w-16" : "w-60"
      )}
    >
      <div className="flex h-16 items-center justify-center border-b border-border-subtle px-4">
        <NavLink to="/" end className="block" aria-label="Shere-e-Punjab Homes admin">
          <img
            src="/logo.png"
            alt="Shere-e-Punjab Homes"
            className={cn(
              "object-contain transition-all",
              collapsed ? "h-9 w-9" : "h-12 w-auto"
            )}
          />
        </NavLink>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-2 py-4">
        {SECTIONS.map((section) => {
          if (section.superAdminOnly && !isSuperAdmin) return null;
          return (
            <div key={section.title ?? "top"}>
              {section.title && !collapsed && (
                <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavItem key={item.to} {...item} collapsed={collapsed} />
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
